<template>
  <div
    class="hero-banner critical-component-clear"
    :class="{
      'no-padding-top': !liquid.sectionPaddingTop,
      'no-padding-bottom': !liquid.sectionPaddingBottom,
      'short-hero-banner': liquid.enableShortHero,
    }"
  >
    <div v-if="!liquid.enableDoubleHero">
      <swiper
        :effect="effect"
        :instance-name="liquid.sectionId"
        :loop="blocks.length > 1 ? true : false"
        :modules="modules"
        :navigation="navigation"
        :pagination="pagination"
        :slides-per-view="1"
        :autoplay="autoplay"
        :speed="speed"
        @init="onInit"
        @slide-change="onSlideChange"
      >
        <swiper-slide
          v-for="(block, index) in blocks"
          :id="`hero-banner-slide-${index}-${liquid.sectionId}`"
          :key="`hero-slide-${index}`"
          class="hero-banner__slide"
          :class="[block.contentWidth, block.blockPosition, `background-color background-color--background-${block.backgroundColor}`]"
          :data-color="block.desktopColor"
        >
          <div
            ref="media"
            class="hero-banner__media"
          >
            <ArtDirection
              v-if="block.image && isDesktop && block.mediaTypeDesktop === 'image' ||
                !isDesktop && block.mediaType ==='image'"
              :breakpoint-sizes="'640-1152-1536-2048-2460-2880'"
              :block-class="'hero-banner'"
              :image-desktop="block.imageDesktop"
              :image="block.image"
              :ratio-desktop="block.contentWidth === 'half' ? '4:3' : '2:1'"
              :ratio="'15:14'"
              :lazyload="index > 0"
              :padding="true"
            />
            <HeroBannerVideo
              v-if="isDesktop && block.mediaTypeDesktop === 'video' ||
                !isDesktop && block.mediaType ==='video'
              "
              :block-class="'hero-banner'"
              :padding="true"
              :poster="isDesktop ? block.imageDesktop?.src : block.image?.src"
              :ratio-desktop="block.contentWidth === 'half' ? '4:3' : '2:1'"
              :ratio="'15:14'"
              :video-src="isDesktop ? block.videoDesktop : block.video"
            />
          </div>

          <div
            class="hero-banner__content"
            :class="[block.desktopColor, block.contentPosition]"
          >
            <div
              class="hero-banner__inner"
              :class="block.textAlign"
            >
              <Eyebrow
                v-if="block.eyebrow"
                :text="block.eyebrow"
                :enable-bullet="block.eyebrowEnableBullet"
                :modifier="'label hero-banner__eyebrow'"
              />

              <HighlighterTitle
                v-if="block.desktopTitle"
                :classes="`h1 hero-banner__title hero-banner__title--desktop ${!block.desktopDescription ? 'hero-banner__title--no-subheading' : ''}`"
                :highlighter-copy="block.desktopTitle"
                :highlighter-size="block.headerSize"
                :highlighter-color="block.highlightColorHeading"
              />

              <div
                v-if="block.desktopDescription"
                class="body-1 hero-banner__description hero-banner__description--desktop"
                v-html="block.desktopDescription"
              />

              <HighlighterTitle
                v-if="block.mobileTitle"
                :classes="`h1 hero-banner__title hero-banner__title--mobile ${!block.mobileDescription ? 'hero-banner__title--no-subheading' : ''}`"
                :highlighter-copy="block.mobileTitle"
                :highlighter-size="block.headerSize"
                :highlighter-color="block.highlightColorHeading"
              />
              <div
                v-if="block.mobileDescription"
                class="hero-banner__description hero-banner__description--mobile"
                v-html="block.mobileDescription"
              />

              <Btn
                v-if="block.ctaText1 && block.ctaLink1"
                :modifiers="[block.ctaLinkStyle1, block.contentWidth, block.ctaLinkStyleMobile1]"
                :url="block.ctaLink1"
                :label="block.ctaText1"
                :custom-element="'a'"
              />

              <Btn
                v-if="block.ctaText2 && block.ctaLink2"
                :modifiers="[block.ctaLinkStyle2, block.contentWidth, block.ctaLinkStyleMobile2]"
                :url="block.ctaLink2"
                :label="block.ctaText2"
                :custom-element="'a'"
              />
            </div>
          </div>
        </swiper-slide>
      </swiper>

      <template v-if="isDesktop && blocks.length > 1">
        <button
          :aria-label="'Previous slide'"
          :class="[arrowColor, `swiper-button-prev-${liquid.sectionId}`]"
          class="swiper-arrow swiper-button-prev"
        >
          <span
            class="button__icon button-icon"
            v-html="$icon('enclosed.chevron.left')"
          />
        </button>

        <button
          :aria-label="'Next slide'"
          :class="[arrowColor, `swiper-button-next-${liquid.sectionId}`]"
          class="swiper-arrow swiper-button-next"
        >
          <span
            class="button__icon button-icon"
            v-html="$icon('enclosed.chevron.right')"
          />
        </button>
      </template>
      <div
        class="swiper-pagination"
        :class="`${isDesktop ? desktopColor : 'light'} swiper-pagination-${liquid.sectionId} swiper-pagination-clickable swiper-pagination-bullets swiper-pagination-horizontal`"
        :style="{top: `${mobilePaginationTopOffset}`}"
      />
    </div>
    <div class="double-hero-container" :style="{ height: `${liquid.enableShortHero ? '375px' : '585px'}` }" v-else>
      <div class="hero-container">
        <div
          ref="media"
          class="hero-banner__media"
        >
          <ArtDirection
            v-if="blocks[0].image && isDesktop && blocks[0].mediaTypeDesktop === 'image' ||
              !isDesktop && blocks[0].mediaType ==='image'"
            :breakpoint-sizes="'640-1152-1536-2048-2460-2880'"
            :block-class="'hero-banner'"
            :image-desktop="blocks[0].imageDesktop"
            :image="blocks[0].image"
            :ratio-desktop="blocks[0].contentWidth === 'half' ? '4:3' : '2:1'"
            :ratio="'15:14'"
            :lazyload="index > 0"
            :padding="true"
          />
          <HeroBannerVideo
            v-if="isDesktop && blocks[0].mediaTypeDesktop === 'video' ||
              !isDesktop && blocks[0].mediaType ==='video'
            "
            :block-class="'hero-banner'"
            :padding="true"
            :poster="isDesktop ? blocks[0].imageDesktop?.src : blocks[0].image?.src"
            :ratio-desktop="blocks[0].contentWidth === 'half' ? '4:3' : '2:1'"
            :ratio="'15:14'"
            :video-src="isDesktop ? blocks[0].videoDesktop : blocks[0].video"
          />
        </div>

        <div
          class="hero-banner__content"
          :class="[blocks[0].desktopColor, blocks[0].contentPosition]"
        >
          <div
            class="hero-banner__inner"
            :class="blocks[0].textAlign"
          >
            <Eyebrow
              v-if="blocks[0].eyebrow"
              :text="blocks[0].eyebrow"
              :enable-bullet="blocks[0].eyebrowEnableBullet"
              :modifier="'label hero-banner__eyebrow'"
            />

            <HighlighterTitle
              v-if="blocks[0].desktopTitle"
              :classes="`h1 hero-banner__title hero-banner__title--desktop ${!blocks[0].desktopDescription ? 'hero-banner__title--no-subheading' : ''}`"
              :highlighter-copy="blocks[0].desktopTitle"
              :highlighter-size="blocks[0].headerSize"
              :highlighter-color="blocks[0].highlightColorHeading"
            />

            <div
              v-if="blocks[0].desktopDescription"
              class="body-1 hero-banner__description hero-banner__description--desktop"
              v-html="blocks[0].desktopDescription"
            />

            <HighlighterTitle
              v-if="blocks[0].mobileTitle"
              :classes="`h1 hero-banner__title hero-banner__title--mobile ${!blocks[0].mobileDescription ? 'hero-banner__title--no-subheading' : ''}`"
              :highlighter-copy="blocks[0].mobileTitle"
              :highlighter-size="blocks[0].headerSize"
              :highlighter-color="blocks[0].highlightColorHeading"
            />
            <div
              v-if="blocks[0].mobileDescription"
              class="hero-banner__description hero-banner__description--mobile"
              v-html="blocks[0].mobileDescription"
            />

            <Btn
              v-if="blocks[0].ctaText1 && blocks[0].ctaLink1"
              :modifiers="[blocks[0].ctaLinkStyle1, blocks[0].contentWidth, blocks[0].ctaLinkStyleMobile1]"
              :url="blocks[0].ctaLink1"
              :label="blocks[0].ctaText1"
              :custom-element="'a'"
            />

            <Btn
              v-if="blocks[0].ctaText2 && blocks[0].ctaLink2"
              :modifiers="[blocks[0].ctaLinkStyle2, blocks[0].contentWidth, blocks[0].ctaLinkStyleMobile2]"
              :url="blocks[0].ctaLink2"
              :label="blocks[0].ctaText2"
              :custom-element="'a'"
            />
          </div>
        </div>
      </div>
      <div class="hero-container">
        <div
          ref="media"
          class="hero-banner__media"
        >
          <ArtDirection
            v-if="blocks[1].image && isDesktop && blocks[1].mediaTypeDesktop === 'image' ||
              !isDesktop && blocks[1].mediaType ==='image'"
            :breakpoint-sizes="'640-1152-1536-2048-2460-2880'"
            :block-class="'hero-banner'"
            :image-desktop="blocks[1].imageDesktop"
            :image="blocks[1].image"
            :ratio-desktop="blocks[1].contentWidth === 'half' ? '4:3' : '2:1'"
            :ratio="'15:14'"
            :lazyload="index > 0"
            :padding="true"
          />
          <HeroBannerVideo
            v-if="isDesktop && blocks[1].mediaTypeDesktop === 'video' ||
              !isDesktop && blocks[1].mediaType ==='video'
            "
            :block-class="'hero-banner'"
            :padding="true"
            :poster="isDesktop ? blocks[1].imageDesktop?.src : blocks[1].image?.src"
            :ratio-desktop="blocks[1].contentWidth === 'half' ? '4:3' : '2:1'"
            :ratio="'15:14'"
            :video-src="isDesktop ? blocks[1].videoDesktop : blocks[1].video"
          />
        </div>

        <div
          class="hero-banner__content"
          :class="[blocks[1].desktopColor, blocks[1].contentPosition]"
        >
          <div
            class="hero-banner__inner"
            :class="blocks[1].textAlign"
          >
            <Eyebrow
              v-if="blocks[1].eyebrow"
              :text="blocks[1].eyebrow"
              :enable-bullet="blocks[1].eyebrowEnableBullet"
              :modifier="'label hero-banner__eyebrow'"
            />

            <HighlighterTitle
              v-if="blocks[1].desktopTitle"
              :classes="`h1 hero-banner__title hero-banner__title--desktop ${!blocks[1].desktopDescription ? 'hero-banner__title--no-subheading' : ''}`"
              :highlighter-copy="blocks[1].desktopTitle"
              :highlighter-size="blocks[1].headerSize"
              :highlighter-color="blocks[1].highlightColorHeading"
            />

            <div
              v-if="blocks[1].desktopDescription"
              class="body-1 hero-banner__description hero-banner__description--desktop"
              v-html="blocks[1].desktopDescription"
            />

            <HighlighterTitle
              v-if="blocks[1].mobileTitle"
              :classes="`h1 hero-banner__title hero-banner__title--mobile ${!blocks[1].mobileDescription ? 'hero-banner__title--no-subheading' : ''}`"
              :highlighter-copy="blocks[1].mobileTitle"
              :highlighter-size="blocks[1].headerSize"
              :highlighter-color="blocks[1].highlightColorHeading"
            />
            <div
              v-if="blocks[1].mobileDescription"
              class="hero-banner__description hero-banner__description--mobile"
              v-html="blocks[1].mobileDescription"
            />

            <Btn
              v-if="blocks[1].ctaText1 && blocks[1].ctaLink1"
              :modifiers="[blocks[1].ctaLinkStyle1, blocks[1].contentWidth, blocks[1].ctaLinkStyleMobile1]"
              :url="blocks[1].ctaLink1"
              :label="blocks[1].ctaText1"
              :custom-element="'a'"
            />

            <Btn
              v-if="blocks[1].ctaText2 && blocks[1].ctaLink2"
              :modifiers="[blocks[1].ctaLinkStyle2, blocks[1].contentWidth, blocks[1].ctaLinkStyleMobile2]"
              :url="blocks[1].ctaLinkStyle2"
              :label="blocks[1].ctaText2"
              :custom-element="'a'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * A full width banner with up to 4 slides contain an image or video, text and cta.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
import ArtDirection from '~global/images/art-direction'
import HeroBannerVideo from '~async/hero-banner/hero-banner-video'
import Btn from '~global/btn/btn'
import Eyebrow from '~global/eyebrow/eyebrow'
import HighlighterTitle from '~global/highlighter-title/highlighter-title'

import { Pagination, A11y, Navigation, EffectFade, Autoplay } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { values } from '~/config/breakpoints'
import { debounce } from '~/helpers/general'
import { ref } from "vue"

export default {
  name: 'HeroBanner',

  components: {
    ArtDirection,
    Btn,
    Eyebrow,
    HighlighterTitle,
    Swiper,
    SwiperSlide,
    HeroBannerVideo,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        paddingBottom: '',
        paddingTop: '',
      }),
    },
    blocks: {
      type: Array,
      default: () => ([]),
    },
  },

  setup(props) {
    let desktopColor = ref('')

    const slideChangeColor = (swiper) => {
      const swiperWrapper = swiper.$wrapperEl[0]
      if (!swiperWrapper) { return }
      const container = swiperWrapper.closest(`#shopify-section-${props.liquid.sectionId}`)
      const currentSlide = swiperWrapper.querySelectorAll('.swiper-slide')[swiper.activeIndex]
      const color = currentSlide.dataset.color
      desktopColor.value = color
      const removeColor = color === 'dark' ? 'light' : 'dark'
      const arrows = container.querySelectorAll('.swiper-arrow')

      arrows.forEach(function(el) {
        el.classList.remove(removeColor)
        el.classList.add(color)
      })
    }

    const onInit = (swiper) => {
      setSlideHeight(swiper);
      slideChangeColor(swiper)
    }

    const onSlideChange = (swiper) => {
      slideChangeColor(swiper)
    }

    const modules = [
        A11y,
        Pagination,
        Navigation,
        EffectFade,
    ]

    if (props.liquid.autoplayEnabled) {
      modules.push(Autoplay)
    }

    function setSlideHeight(swiper){
      // const heroBannerElement = document.getElementsByClassName('short-hero-banner')[0];
      // if(heroBannerElement) heroBannerElement.classList.remove('short-hero-banner');
      const heroBannerImageContainers = document.getElementsByClassName('hero-banner__image-container');
      Array.from(heroBannerImageContainers).forEach(container => {
        container.style.height = '100%';
      })

      const swiperWrapper = swiper.$wrapperEl[0]
      if (!swiperWrapper) { return }
      const swiperElements = document.querySelectorAll('.swiper-wrapper,.swiper-slide');
      const screenWidth = screen.width >= 937;
      swiperElements.forEach(el => {
        el.style.height = props.liquid.enableShortHero && screenWidth ? '375px' : 'auto';
      })
      swiper.update();
    }

    return {
      onInit,
      onSlideChange,
      desktopColor,
      modules: modules,
      autoplay: props.liquid.autoplayEnabled ?
        { delay: props.liquid.autoplayDelay ? Number(props.liquid.autoplayDelay) * 1000 : 5000 }
        : undefined,
      effect: props.liquid.effect,
      speed: props.liquid.speed,
      navigation: {
        nextEl: `.swiper-button-next-${props.liquid.sectionId}`,
        prevEl: `.swiper-button-prev-${props.liquid.sectionId}`,
      },
      pagination: {
        clickable: true,
        el: `.swiper-pagination-${props.liquid.sectionId}`,
        type: 'bullets',
      },
    }
  },

  data() {
    return {
      highLightHeading: {
        none: '',
        blue: 'highlight highlight--accent02',
        yellow: 'highlight highlight--accent01',
        pink: 'highlight highlight--accent03',
      },
      buttonStyle: {
        pink: '',
        pinkOutlined: '',
        dark: '',
        darkOutline: '',
        light: '',
        lightOutlined: '',
      },
      mobilePaginationTopOffset: '',
      isDesktop: true,
      arrowColor: '',
    }
  },

  computed: {

  },

  mounted() {
    this.resize()
    window.addEventListener('resize', debounce(() => {
      this.resize()
    }, 500))
  },

  methods: {
    resize: function() {
      if (window.innerWidth >= values.l) {
        this.mobilePaginationTopOffset = '100%'
        this.isDesktop = true
      } else {
        const { media } = this.$refs
        if (media && media[0]) {
          const mobileImage = media[0]
          const noPaddingTop = !this.liquid.sectionPaddingTop
          const offsetBump = noPaddingTop ? -24 : 5
          this.mobilePaginationTopOffset = `${mobileImage.offsetHeight + offsetBump}px`
        }
        this.isDesktop = false
      }
    },
  },
}
</script>

<style lang="scss">
@import './hero-banner';
</style>
